import { ChevronRight, ClipboardList, Plus } from 'lucide-react';
import { Button } from '../../components/Common/button';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import questionnaireService from '../../services/questionnaireService';


const QuestionnairesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await questionnaireService.list();
        if (!mounted) return;
        const questionnairesData = Array.isArray(data) ? data : [];
        setQuestionnaires(questionnairesData);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.detail || e?.message || t('questionnairesPage.loadError'));
        setQuestionnaires([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleViewQuestionnaire = (id: string) => {
    // Navigate to questionnaire detail or edit page
    navigate(`/questionnaires/${id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">{t('questionnairesPage.title')}</h1>
        <Button onClick={() => navigate('/questionnaires')} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {t('questionnairesPage.newQuestionnaire')}
        </Button>
      </div>
      
      {loading && (
        <div className="text-center text-muted-foreground">{t('questionnairesPage.loading')}</div>
      )}
      {error && (
        <div className="bg-destructive/10 border-l-4 border-destructive text-destructive p-4 rounded mb-4">
          {error}
        </div>
      )}

      {!loading && questionnaires.length === 0 && !error && (
        <div className="text-center py-12 border border-border rounded-lg bg-card">
          <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t('questionnairesPage.noQuestionnaires')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('questionnairesPage.noQuestionnairesDescription')}
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/questionnaires')}>
              <Plus className="h-4 w-4 mr-2" />
              {t('questionnairesPage.createQuestionnaire')}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {questionnaires.map((questionnaire: any) => {
          const questionCount = questionnaire.questions?.length || 0;
          
          return (
            <div 
              key={questionnaire.id} 
              className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleViewQuestionnaire(questionnaire.id)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="bg-primary/10 p-3 rounded-md">
                      <ClipboardList className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-semibold text-foreground">{questionnaire.name}</h2>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {questionnaire.description || t('questionnairesPage.noDescription')}
                      </p>
                      
                      <div className="mt-4 flex flex-wrap items-center gap-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {t('questionnairesPage.questionsCount', { count: questionCount })}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t('questionnairesPage.template')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="w-8 h-8 rounded-full ml-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewQuestionnaire(questionnaire.id);
                    }}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuestionnairesPage;